import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { simulateAnalysis } from '../services/staticAnalysis';
import Header from '../components/Header';
import '../App.css';

const SeguridadNacionalAnalisis = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [modalContent, setModalContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Datos de análisis crítico
  const criticalAnalysis = [
    {
      id: 'imperial_map',
      title: 'El Nuevo Mapa Imperial',
      subtitle: 'Redefiniendo Esferas de Influencia',
      icon: '🗺️',
      description: 'Análisis de cómo la nueva estrategia redefine las zonas de influencia global',
      insights: [
        'División del mundo en "zonas de interés vital" americano',
        'Retorno a la geopolítica de esferas de influencia del siglo XIX',
        'Rechazo explícito al multilateralismo y orden basado en reglas'
      ],
      context: 'La estrategia abandona el pretexto de un orden mundial basado en reglas para adoptar abiertamente una lógica imperial de esferas de influencia.'
    },
    {
      id: 'economic_warfare',
      title: 'Guerra Económica Total',
      subtitle: 'Mercantilismo del Siglo XXI',
      icon: '💰',
      description: 'El comercio como extensión de la política por otros medios',
      insights: [
        'Aranceles como herramienta de política exterior',
        'Desacoplamiento económico selectivo con adversarios',
        'Uso de sanciones como arma de destrucción masiva económica'
      ],
      context: 'Transformación del comercio internacional de un juego de suma positiva a un juego de suma cero donde el dominio económico es supervivencia nacional.'
    },
    {
      id: 'alliance_system',
      title: 'Sistema de Alianzas Transaccional',
      subtitle: 'Lealtad por Contrato',
      icon: '🤝',
      description: 'Redefinición de alianzas basada en contribuciones cuantificables',
      insights: [
        'Alianzas condicionadas al gasto militar del 5% del PIB',
        'Abandono de compromisos de seguridad no rentables',
        'Priorización de socios que compran armamento americano'
      ],
      context: 'Las alianzas tradicionales basadas en valores compartidos son reemplazadas por relaciones transaccionales basadas en contribuciones militares y compras de defensa.'
    },
    {
      id: 'technological_dominance',
      title: 'Supremacía Tecnológica',
      subtitle: 'La Nueva Carrera Espacial',
      icon: '🚀',
      description: 'Control de tecnologías críticas como arma estratégica',
      insights: [
        'Monopolización de semiconductores avanzados',
        'Militarización del espacio como dominio de combate',
        'Guerra de información y control de narrativas'
      ],
      context: 'La tecnología se convierte en el nuevo campo de batalla donde el control de la innovación determina la supremacía geopolítica del siglo XXI.'
    }
  ];

  const regionalAnalysis = {
    'China': {
      threat: 'Rival Sistémico',
      strategy: 'Contención Total',
      tools: ['Desacoplamiento tecnológico', 'Cerco militar', 'Guerra comercial'],
      assessment: 'China es vista como el único rival capaz de desafiar la hegemonía americana. La estrategia busca contener su ascenso por todos los medios necesarios.'
    },
    'Europa': {
      threat: 'Aliado Dependiente',
      strategy: 'Subordinación Benevolente',
      tools: ['Dependencia energética', 'Protección militar', 'Presión comercial'],
      assessment: 'Europa debe elegir entre autonomía estratégica y protección americana. La estrategia busca mantener la dependencia europea.'
    },
    'Rusia': {
      threat: 'Potencia Revisionista',
      strategy: 'Aislamiento Selectivo',
      tools: ['Sanciones masivas', 'Expansión OTAN', 'Guerra proxy'],
      assessment: 'Rusia es vista como una amenaza declinante pero peligrosa. El objetivo es acelerar su declive sin provocar escalada nuclear.'
    },
    'Medio Oriente': {
      threat: 'Zona de Caos',
      strategy: 'Gestión de Crisis',
      tools: ['Equilibrio de poder', 'Venta de armas', 'Intervención selectiva'],
      assessment: 'El Medio Oriente es visto como una región a gestionar, no a transformar. El objetivo es mantener el equilibrio y asegurar el flujo energético.'
    }
  };

  const handleAnalyzeSection = async (section) => {
    setIsLoading(true);
    setIsModalOpen(true);
    setModalContent('Generando análisis crítico...');

    try {
      const response = await simulateAnalysis("security", section.title);
      setModalContent(response);
    } catch (error) {
      console.error('Error analyzing section:', error);
      setModalContent(`**Error de Análisis:** No se pudo cargar el análisis.\n\n*Por favor intenta nuevamente.*`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegionAnalysis = async (regionName, regionData) => {
    setIsLoading(true);
    setIsModalOpen(true);
    setModalContent('Analizando estrategia regional...');

    try {
      const response = await simulateAnalysis("security", regionName);
      setModalContent(response);
    } catch (error) {
      console.error('Error analyzing region:', error);
      setModalContent(`**Error de Análisis:** No se pudo cargar el análisis.\n\n*Por favor intenta nuevamente.*`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Link to="/">
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              ← Volver al Inicio
            </button>
          </Link>
        </div>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            marginBottom: '1rem',
            color: '#f1f5f9',
            background: 'linear-gradient(135deg, #e63946 0%, #f1faee 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            🔍 Análisis Crítico
          </h1>
          <h2 style={{ 
            fontSize: '1.5rem', 
            color: '#94a3b8',
            marginBottom: '1rem'
          }}>
            El Nuevo Mapa Imperial: Estrategia de Seguridad Nacional 2025
          </h2>
          <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
            Análisis geopolítico profundo con inteligencia artificial
          </p>
        </div>

        {/* Análisis Crítico Principal */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ 
            color: '#f1f5f9', 
            fontSize: '2rem', 
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            📊 Dimensiones Estratégicas
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', 
            gap: '2rem' 
          }}>
            {criticalAnalysis.map((section, index) => (
              <div key={section.id} style={{
                background: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '1rem',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                padding: '2rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = '#e63946';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
              }}>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2.5rem', marginRight: '1rem' }}>{section.icon}</span>
                  <div>
                    <h4 style={{ color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      {section.title}
                    </h4>
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{section.subtitle}</p>
                  </div>
                </div>
                
                <p style={{ color: '#cbd5e1', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  {section.description}
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h5 style={{ color: '#f1f5f9', fontSize: '1rem', marginBottom: '0.75rem' }}>
                    Puntos Clave:
                  </h5>
                  <ul style={{ color: '#94a3b8', fontSize: '0.9rem', paddingLeft: '1.5rem' }}>
                    {section.insights.map((insight, idx) => (
                      <li key={idx} style={{ marginBottom: '0.5rem' }}>{insight}</li>
                    ))}
                  </ul>
                </div>
                
                <div style={{ 
                  padding: '1rem', 
                  background: 'rgba(15, 23, 42, 0.8)', 
                  borderRadius: '0.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    {section.context}
                  </p>
                </div>
                
                <button
                  onClick={() => handleAnalyzeSection(section)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: 'linear-gradient(135deg, #e63946 0%, #dc2626 100%)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(230, 57, 70, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  🤖 Análisis Profundo con IA
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Análisis Regional */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ 
            color: '#f1f5f9', 
            fontSize: '2rem', 
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            🌍 Estrategias Regionales
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {Object.entries(regionalAnalysis).map(([region, data], index) => (
              <div key={region} style={{
                background: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '1rem',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.borderColor = '#fbbf24';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
              }}>
                
                <h4 style={{ color: '#f1f5f9', fontSize: '1.3rem', marginBottom: '1rem' }}>
                  {region}
                </h4>
                
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#94a3b8' }}>Amenaza:</span>
                    <span style={{ color: '#f1f5f9', fontWeight: 'bold' }}>{data.threat}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#94a3b8' }}>Estrategia:</span>
                    <span style={{ color: '#fbbf24' }}>{data.strategy}</span>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <h5 style={{ color: '#f1f5f9', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Herramientas:
                  </h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {data.tools.map((tool, idx) => (
                      <span key={idx} style={{
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        borderRadius: '0.25rem',
                        fontSize: '0.8rem'
                      }}>
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
                
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {data.assessment}
                </p>
                
                <button
                  onClick={() => handleRegionAnalysis(region, data)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #fbbf24',
                    background: 'rgba(251, 191, 36, 0.1)',
                    color: '#fbbf24',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fbbf24';
                    e.currentTarget.style.color = '#0f172a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(251, 191, 36, 0.1)';
                    e.currentTarget.style.color = '#fbbf24';
                  }}
                >
                  🎯 Analizar Estrategia
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '3rem', 
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <Link to="/seguridad-nacional-2025">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              📋 Estrategia Principal
            </button>
          </Link>
          
          <Link to="/">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'rgba(148, 163, 184, 0.2)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ← Dashboard
            </button>
          </Link>
        </div>
      </div>

      {/* Modal para análisis IA */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>
                🔍 Análisis Geopolítico Crítico
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              {isLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤖</div>
                  <p>Generando análisis crítico...</p>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    background: 'rgba(148, 163, 184, 0.2)', 
                    borderRadius: '2px',
                    marginTop: '1rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '30%',
                      height: '100%',
                      background: 'linear-gradient(90deg, #e63946, #fbbf24)',
                      borderRadius: '2px',
                      animation: 'loading 2s ease-in-out infinite'
                    }} />
                  </div>
                </div>
              ) : (
                <div 
                  style={{ 
                    color: '#cbd5e1',
                    lineHeight: '1.8',
                    fontSize: '1.1rem'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: modalContent
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f1f5f9; font-weight: bold;">$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em style="color: #fbbf24;">$1</em>')
                      .replace(/\n\n/g, '</p><p style="margin: 1.5rem 0;">')
                      .replace(/^/, '<p style="margin: 1.5rem 0;">')
                      .replace(/$/, '</p>')
                      .replace(/(\d+\.\s)/g, '<br/><strong style="color: #e63946;">$1</strong>')
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeguridadNacionalAnalisis;